import { Button, HStack, useDisclosure, Stack, Text } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FullModal } from '$/components/common/Modals/Modal';
import {
  HookFormDropdownInput,
  type HookFormDropdownInputProps,
} from '$/components/core/Form/HookFormDropdownInput';
import {
  HookFormInput,
  type HookFormInputProps,
} from '$/components/core/Form/HookFormInput';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { useToast } from '$/hooks/useToast';
import { type User } from '$/services/usecases/authentication/mapper/genericCloudResponse';
import { type SchoolClass } from '$/services/usecases/school/mapper/schoolClasses';
import { editStudent } from '$/services/usecases/school/students';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  schoolClassOptions: SchoolClass[];
  student: User;
}

type EditStudentFormData = {
  schoolClassId: string;
  query: string;
  expirationDate?: string;
};

const Input = (props: HookFormInputProps<EditStudentFormData>) => (
  <HookFormInput<EditStudentFormData> {...props} />
);

const Dropdown = (props: HookFormDropdownInputProps<EditStudentFormData>) => (
  <HookFormDropdownInput<EditStudentFormData> {...props} />
);

export const EditStudentModal: FC<Props> = ({
  isOpen,
  onClose,
  schoolClassOptions,
  student,
}) => {
  const { t } = useTranslation();
  const { cloudMessageTranslation } = useCloudMessageTranslation();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen: isDropdownOpen, onToggle: onToggleDropdown } =
    useDisclosure();
  const { mutate } = useMutation({
    mutationFn: editStudent,
    onSettled: () => {
      setIsLoading(false);
    },
    onSuccess: (data) => {
      if (!data.isSuccessful) {
        toast(cloudMessageTranslation(data.response.message), 'error', '');
        return;
      }

      toast(
        t('school.schoolClasses.editStudent.toastTitle'),
        'success',
        t('school.schoolClasses.editStudent.toastDescription'),
      );
      onClose();
      formMethods.reset();
    },
  });

  const formMethods = useForm<EditStudentFormData>();

  useEffect(() => {
    if (student != null) {
      const selectedSchoolClass = schoolClassOptions.find(
        (schoolClass) => schoolClass.id === student.classId,
      );
      formMethods.reset({
        schoolClassId: student.classId,
        expirationDate: student.expirationDate,
        query: selectedSchoolClass?.name ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student]);

  const onSubmit = (data: EditStudentFormData) => {
    setIsLoading(true);
    mutate({
      studentId: student.id,
      schoolClassId: data.schoolClassId,
      expirationDate: data.expirationDate,
    });
  };

  const onCloseModal = () => {
    onClose();
    formMethods.reset();
  };

  return (
    <FullModal
      isOpen={isOpen}
      onClose={onClose}
      header={t('school.students.editStudentButton')}
      isScrollable={false}
    >
      <FormProvider {...formMethods}>
        <Stack as='form' gap='4' onSubmit={formMethods.handleSubmit(onSubmit)}>
          <Dropdown
            accessor='query'
            label={t('school.schoolClasses.inviteStudent.classDropdownLabel')}
            dropdownOpen={isDropdownOpen}
            placeholder={t(
              'school.schoolClasses.inviteStudent.classDropdownPlaceholder',
            )}
            toggleDropdown={onToggleDropdown}
            dropdownContent={() => (
              <Stack pt='2' pb='6px'>
                {schoolClassOptions?.map((schoolClass) => (
                  <Button
                    key={schoolClass.id}
                    px='4'
                    py='6px'
                    fontSize='sm'
                    fontWeight='normal'
                    textAlign='left'
                    _hover={{ bg: 'bodyBackground' }}
                    cursor='pointer'
                    onClick={() => {
                      formMethods.setValue('schoolClassId', schoolClass.id, {
                        shouldDirty: true,
                      });
                      formMethods.setValue('query', schoolClass.name, {
                        shouldDirty: true,
                      });
                      onToggleDropdown();
                    }}
                    type='button'
                    variant='unstyled'
                  >
                    <HStack>
                      <Text>{schoolClass.name}</Text>
                    </HStack>
                  </Button>
                ))}
              </Stack>
            )}
          />
          <Input
            accessor='expirationDate'
            type='date'
            min={new Date().toISOString().split('T')[0]}
            label={t('school.students.expirationDateLabel')}
          />
          <HStack justify='space-between' w='full' mt='10'>
            <Button
              isLoading={isLoading}
              onClick={onCloseModal}
              type='button'
              variant='ghost'
            >
              {t('general.cancel')}
            </Button>
            <Button
              isDisabled={
                !formMethods.formState.isValid || !formMethods.formState.isDirty
              }
              isLoading={isLoading}
              type='submit'
            >
              {t('general.save')}
            </Button>
          </HStack>
        </Stack>
      </FormProvider>
    </FullModal>
  );
};
